import CreateAxiosInstance from './axiosConfig';
const APIClient = CreateAxiosInstance(import.meta.env.VITE_API_BASE_URL);

// Admin — Referral Programs & Tiers

export async function listReferralPrograms() {
  return await APIClient.get('/admin/referral/programs', {
    defaultErrorMessage: 'Error getting referral programs',
  });
}

export async function createReferralProgram(program) {
  return await APIClient.post('/admin/referral/programs', program, {
    defaultErrorMessage: 'Error creating referral program',
  });
}

export async function getReferralProgram(programId) {
  return await APIClient.get(`/admin/referral/programs/${programId}`, {
    defaultErrorMessage: 'Error getting referral program',
  });
}

export async function updateReferralProgram(programId, data) {
  return await APIClient.put(`/admin/referral/programs/${programId}`, data, {
    defaultErrorMessage: 'Error updating referral program',
  });
}

export async function createReferralTier(programId, tier) {
  return await APIClient.post(`/admin/referral/programs/${programId}/tiers`, tier, {
    defaultErrorMessage: 'Error creating referral tier',
  });
}

export async function updateReferralTier(programId, tierId, data) {
  return await APIClient.put(`/admin/referral/programs/${programId}/tiers/${tierId}`, data, {
    defaultErrorMessage: 'Error updating referral tier',
  });
}

export async function deleteReferralTier(programId, tierId) {
  return await APIClient.delete(`/admin/referral/programs/${programId}/tiers/${tierId}`, {
    defaultErrorMessage: 'Error deleting referral tier',
  });
}

// Admin — Referrals (fraud review). Unlike the teacher-facing list, this surface is
// deliberately unmasked — callers pass `params` already built (see buildListParams in
// AdminTools/Referral/referralAdminHelpers.js) rather than raw filters.

export async function listReferrals(params) {
  return await APIClient.get('/admin/referral/referrals', {
    params,
    defaultErrorMessage: 'Error getting referrals',
  });
}

export async function voidReferral(referralId, reason) {
  return await APIClient.post(
    `/admin/referral/referrals/${referralId}/void`,
    { reason },
    { defaultErrorMessage: 'Error voiding referral' }
  );
}

// Manual claim tool for "this person should have been credited". Every rejection
// reason (self-referral, referrer is a guest, referee already referred, user not
// found) comes back as a specific `err.response.data.message` — callers should show
// that verbatim rather than the defaultErrorMessage fallback below.
export async function attachReferral(payload) {
  return await APIClient.post('/admin/referral/referrals/attach', payload, {
    defaultErrorMessage: 'Error attaching referral',
  });
}

// Admin — Grants (swag fulfilment queue).

export async function listGrants(params) {
  return await APIClient.get('/admin/referral/grants', {
    params,
    defaultErrorMessage: 'Error getting grants',
  });
}

export async function voidGrant(grantId, reason) {
  return await APIClient.post(
    `/admin/referral/grants/${grantId}/void`,
    { reason },
    { defaultErrorMessage: 'Error voiding grant' }
  );
}

export async function redeemGrant(grantId) {
  return await APIClient.post(
    `/admin/referral/grants/${grantId}/redeem`,
    {},
    { defaultErrorMessage: 'Error redeeming grant' }
  );
}

// Admin — Referrers (per-program qualified-count leaderboard). Assignment-scoped: a
// referrer with no assignment to this program is absent even if they have qualified
// referrals elsewhere.
export async function listProgramReferrers(programId, params) {
  return await APIClient.get(`/admin/referral/programs/${programId}/referrers`, {
    params,
    defaultErrorMessage: 'Error getting program referrers',
  });
}

// User-facing — a teacher's own referral status.

// A 404 here is handled quietly rather than through the global error modal — the caller
// renders "not available yet" instead. Anything else still surfaces normally.
export async function getMyReferralStatus() {
  return await APIClient.get('/referral/me', {
    customErrorHandler: () => {},
  });
}

// Enrols the caller in the program behind `surface`'s key and returns the same payload as
// getMyReferralStatus. A share surface calls this instead of the plain status read: the signup
// enrolment hook only fires on account creation, so a teacher who already had an account is
// otherwise never in the program the offer advertises.
//
// `surface` is a name the server maps to a key (see ENROLMENT_KEY_BY_SURFACE) — deliberately not
// the key itself, so a caller can't opt into a campaign it was never shown. An unrecognised
// surface is logged server-side and returns the summary unenrolled rather than failing.
//
// Same silent 404 handling and same response shape as getMyReferralStatus, so callers can swap
// one for the other.
export async function enrollInReferralProgram(surface) {
  return await APIClient.post('/referral/enroll', { surface }, { customErrorHandler: () => {} });
}

export async function getMyReferrals(page = 1, perPage = 20) {
  return await APIClient.get('/referral/me/referrals', {
    params: { page, per_page: perPage },
    customErrorHandler: () => {},
  });
}

// `programKey` names the campaign whose invite copy to send — the same key the backend maps to
// a program through the referral-program-configuration flag. It only picks the email template;
// what a referral earns is still decided by the referrer's assignments. Omitted means the
// default template.
//
// `inviteSource` names the surface this send came from (a value from
// src/constants/linkSources.js) and rides into the invite link's `ref_src`, so a signup is
// attributable to the screen that asked rather than just to "an invite email". Every surface
// passes its own: several of them invite against the same campaign, so `programKey` can't
// distinguish them.
export async function sendReferralInvites(emails = [], programKey = null, inviteSource = null) {
  return await APIClient.post(
    '/referral/invites',
    { emails, program_key: programKey, invite_source: inviteSource },
    { defaultErrorMessage: 'Error sending invites' }
  );
}

// Referee rewards the signed-in user hasn't been told about yet. Silent on error for the
// same reason as getMyReferralStatus: this is polled on app load, and a 404 (flag off) or a
// transient failure must never throw the global error modal at someone who didn't ask for
// anything. No unseen rewards is the overwhelmingly common answer.
export async function getUnseenReferralRewards() {
  return await APIClient.get('/referral/me/rewards/unseen', {
    customErrorHandler: () => {},
  });
}

// Acknowledge rewards after showing them. Silent too — failing to mark seen shows the modal
// again next load, which is a far better outcome than an error dialog.
export async function markReferralRewardsSeen(rewardIds = []) {
  return await APIClient.post(
    '/referral/me/rewards/seen',
    { reward_ids: rewardIds },
    { customErrorHandler: () => {} }
  );
}

// Public, pre-signup lookup of the owner of a share link: returns the referrer's given name
// (and the referee reward their programs promise, which no caller reads yet). Unauthenticated
// by design, so the homepage referral welcome — routes/Base/ReferralWelcomePrompt, its only
// caller — can name the referrer before there is an account.
export async function resolveReferralCode(code) {
  return await APIClient.get(`/referral/resolve/${encodeURIComponent(code)}`, {
    customErrorHandler: () => {},
  });
}

// Public and rate-limited (30/min per IP): callers go through src/ads/referralAdCopy.js, which
// fetches it once per page load. Quiet 404 handling, like the other teacher-facing reads.
export async function getFeaturedReferralProgram() {
  return await APIClient.get('/referral/program/featured', {
    customErrorHandler: () => {},
  });
}
